import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Plus, Trash2, FlaskConical, DollarSign, TrendingUp, RefreshCcw, Beaker, Sparkles, FileSpreadsheet } from "lucide-react";
import CsvImportDialog from "@/components/CsvImportDialog";
import { toast } from "sonner";
import { mixColor } from "@/lib/mixColor";
import BulkMixGlCodesDialog from "@/components/BulkMixGlCodesDialog";

const empty = {
  code: "", name: "", strength_mpa: 25, slump_mm: 80, max_wc_ratio: "", target_wc_ratio: "",
  cement_kg: 320, water_kg: 175, sand_kg: 750, man_dust_kg: 0,
  agg_7mm_kg: 0, agg_10mm_kg: 350, agg_14mm_kg: 0, agg_20mm_kg: 700,
  exposed_rock_kg: 0,          // legacy — auto-sum from exposed_rocks list on save
  exposed_rocks: [],           // v3 · per-type rocks (Basalt 10mm, Quartz 14mm, …)
  // v3.5 · Four canonical additives (ml per m³) — match the plant's dosing skids
  wrda_pa_ml: 0,
  wrda_pn40_ml: 0,
  aea_ml: 0,
  daraset_nc90_ml: 0,
  accelerator_ml: 0,
  retarder_ml: 0,
  superplasticiser_ml: 0,
  price_per_m3: 220,
  gl_code: "",
  myob_item_number: "",
  k412_product: "",
  notes: "",
};

// v3.5 — replaces the freeform additive list. These 4 are hardcoded to match
// the physical dosing lines on the plant (WRDA PA, WRDA PN40, AEA, DARASET NC90).
const ADDITIVE_FIELDS = [
  { k: "wrda_pa_ml",      l: "WRDA PA",      hint: "Plasticiser" },
  { k: "wrda_pn40_ml",    l: "WRDA PN40",    hint: "Mid-range water reducer" },
  { k: "aea_ml",          l: "AEA",          hint: "Air entraining admixture" },
  { k: "daraset_nc90_ml", l: "DARASET NC90", hint: "Non-chloride accelerator" },
  { k: "accelerator_ml",  l: "Accelerator",  hint: "Set accelerator" },
  { k: "retarder_ml",     l: "Retarder",     hint: "Set retarder (hot weather / long haul)" },
  { k: "superplasticiser_ml", l: "Superplasticiser", hint: "High-range water reducer" },
];

const ingredientFields = [
  { k: "cement_kg", l: "Cement (kg)" },
  { k: "water_kg", l: "Water (kg)" },
  { k: "sand_kg", l: "Sand (kg)" },
  { k: "man_dust_kg", l: "Man Dust (kg)" },
  { k: "agg_7mm_kg", l: "7mm Agg (kg)" },
  { k: "agg_10mm_kg", l: "10mm Agg (kg)" },
  { k: "agg_14mm_kg", l: "14mm Agg (kg)" },
  { k: "agg_20mm_kg", l: "20mm Agg (kg)" },
];

export default function Mixes() {
  const [list, setList] = useState([]);
  const [rockItems, setRockItems] = useState([]);   // inventory items category=exposed_rock
  const [open, setOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [bulkGlOpen, setBulkGlOpen] = useState(false);

  const load = () => api.get("/mixes").then((r) => setList(r.data));
  const loadRocks = () => api.get("/inventory").then((r) => {
    setRockItems((r.data || []).filter((i) => i.category === "exposed_rock"));
  });
  const [k412Products, setK412Products] = useState([]);
  useEffect(() => { load(); loadRocks(); api.get("/scale/products").then((r) => setK412Products(r.data || [])).catch(() => {}); }, []);

  // When opening a mix, seed form.exposed_rocks with one row per inventory rock item
  // (existing kg_per_m3 kept, new items default to 0).
  const hydrateRocks = (existing) => {
    const byId = Object.fromEntries((existing || []).map((r) => [r.item_id, r]));
    return rockItems.map((item) => byId[item.id] || {
      item_id: item.id,
      name: item.name,
      sku: item.sku || "",
      kg_per_m3: 0,
      cost_per_kg: Number(item.unit_cost) || 0,
    });
  };

  const openNew = () => {
    setEditing(null);
    setForm({ ...empty, exposed_rocks: hydrateRocks([]) });
    setOpen(true);
  };
  const openEdit = (m) => {
    setEditing(m);
    setForm({ ...empty, ...m, exposed_rocks: hydrateRocks(m.exposed_rocks || []) });
    setOpen(true);
  };

  const updateRock = (item_id, kg) => {
    setForm({
      ...form,
      exposed_rocks: (form.exposed_rocks || []).map((r) =>
        r.item_id === item_id ? { ...r, kg_per_m3: Number(kg) || 0 } : r
      ),
    });
  };

  const save = async () => {
    try {
      // Strip zero-kg rock rows so we don't persist noise
      const body = {
        ...form,
        max_wc_ratio: form.max_wc_ratio === "" || form.max_wc_ratio === null ? null : parseFloat(form.max_wc_ratio),
        target_wc_ratio: form.target_wc_ratio === "" || form.target_wc_ratio === null ? null : parseFloat(form.target_wc_ratio),
        exposed_rocks: (form.exposed_rocks || []).filter((r) => (Number(r.kg_per_m3) || 0) > 0),
      };
      if (editing) await api.put(`/mixes/${editing.id}`, body); else await api.post("/mixes", body);
      toast.success("Saved");
      setOpen(false);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const del = async (id) => { if (!window.confirm("Delete mix?")) return; await api.delete(`/mixes/${id}`); toast.success("Deleted"); load(); };

  const recomputeAll = async () => {
    try {
      const res = await api.post("/mixes/recompute-costs");
      toast.success(`Refreshed cost on ${res.data.updated} mix${res.data.updated === 1 ? "" : "es"}`);
      load();
    } catch (e) { toast.error("Failed"); }
  };

  const profit = (m) => {
    const cost = m.material_cost_per_m3 || 0;
    const sale = m.price_per_m3 || 0;
    return { cost, sale, profit: sale - cost, margin: sale > 0 ? ((sale - cost) / sale) * 100 : 0 };
  };

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="mixes-page">
      <div className="flex items-end justify-between flex-wrap gap-2">
        <div>
          <div className="label-tag">FORMULATIONS</div>
          <h1 className="heading text-3xl font-black uppercase">Mix <span className="text-primary-orange">Designs</span></h1>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" data-testid="bulk-mix-gl-btn" onClick={() => setBulkGlOpen(true)}
                  className="rounded-none border-industrial hover:border-primary-orange bg-transparent text-ink hover:text-primary-orange uppercase tracking-widest text-xs font-bold">
            <Sparkles className="w-4 h-4 mr-1" />Bulk GL Codes
          </Button>
          <Button variant="outline" data-testid="recompute-costs-btn" onClick={recomputeAll}
                  className="rounded-none border-industrial hover:border-primary-orange bg-transparent text-ink hover:text-primary-orange uppercase tracking-widest text-xs font-bold">
            <RefreshCcw className="w-4 h-4 mr-1" />Recompute Costs
          </Button>
          <Button data-testid="import-mixes-btn" variant="outline" onClick={() => setImportOpen(true)} className="rounded-none border-industrial uppercase tracking-widest text-xs font-bold"><FileSpreadsheet className="w-4 h-4 mr-1 text-primary-orange" />Import</Button>
          <Button data-testid="new-mix-btn" onClick={openNew} className="rounded-none bg-primary-orange hover:bg-[rgb(var(--primary-hover))] uppercase tracking-widest text-xs font-bold">
            <Plus className="w-4 h-4 mr-1" />New Mix
          </Button>
        </div>
      </div>

      <BulkMixGlCodesDialog open={bulkGlOpen} onOpenChange={setBulkGlOpen} onDone={load} />

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {list.map((m) => {
          const p = profit(m);
          const mc = mixColor(m.code);
          return (
            <div key={m.id} data-testid={`mix-card-${m.code}`}
                 onClick={() => openEdit(m)}
                 className="card-industrial p-4 cursor-pointer hover:border-primary-orange transition-colors"
                 style={{ borderLeft: `3px solid ${mc.hex}` }}>
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-2">
                  <FlaskConical className="w-5 h-5 text-primary-orange" />
                  <div>
                    <div className="heading font-black text-lg">{m.code}</div>
                    <div className="text-xs text-muted-industrial">{m.name}</div>
                    {m.gl_code && <div className="text-[9px] mono text-muted-industrial mt-0.5">GL {m.gl_code}</div>}
                    {m.k412_product && <div className="text-[9px] mono text-sky-400 mt-0.5" data-testid={`mix-k412-${m.id}`}>K412 {m.k412_product}</div>}
                  </div>
                </div>
                <button onClick={(e) => { e.stopPropagation(); del(m.id); }} className="text-muted-industrial hover:text-primary-orange"><Trash2 className="w-4 h-4" /></button>
              </div>

              <div className="grid grid-cols-5 gap-2 mt-3 text-xs">
                <div><div className="label-tag">Strength</div><div className="mono">{m.strength_mpa} MPa</div></div>
                <div><div className="label-tag">Slump</div><div className="mono">{m.slump_mm} mm</div></div>
                <div><div className="label-tag">Max W/C</div><div className="mono" data-testid={`mix-max-wc-${m.code}`}>{m.max_wc_ratio ? Number(m.max_wc_ratio).toFixed(2) : <span className="text-muted-industrial">{m.cement_kg ? ((m.water_kg + 20) / m.cement_kg).toFixed(2) + " (default)" : "—"}</span>}</div></div>
                <div><div className="label-tag">Target W/C</div><div className="mono text-sky-400 font-bold" data-testid={`mix-target-wc-${m.code}`}>{(m.target_wc_ratio ?? (m.cement_kg ? m.water_kg / m.cement_kg : null)) != null ? Number(m.target_wc_ratio ?? (m.water_kg / m.cement_kg)).toFixed(2) : "—"}</div></div>
                <div><div className="label-tag">Sale</div><div className="mono text-primary-orange font-bold">${m.price_per_m3}</div></div>
              </div>

              {/* Cost / Profit line */}
              <div className="grid grid-cols-3 gap-2 mt-3 pt-3 border-t border-industrial text-xs">
                <div>
                  <div className="label-tag">Cost/m³</div>
                  <div className="mono text-red-500 font-bold">${p.cost.toFixed(2)}</div>
                </div>
                <div>
                  <div className="label-tag">Profit</div>
                  <div className="mono font-bold" style={{ color: p.profit >= 0 ? "#10B981" : "#EF4444" }}>${p.profit.toFixed(2)}</div>
                </div>
                <div>
                  <div className="label-tag">Margin</div>
                  <div className="mono font-bold" style={{ color: p.margin >= 25 ? "#10B981" : p.margin >= 10 ? "#F59E0B" : "#EF4444" }}>{p.margin.toFixed(0)}%</div>
                </div>
              </div>

              <div className="mt-3 pt-3 border-t border-industrial mono text-[10px] text-muted-industrial">
                Cem {m.cement_kg} · Sand {m.sand_kg} {m.man_dust_kg > 0 && <>· MD {m.man_dust_kg}</>} · Agg {(m.agg_7mm_kg + m.agg_10mm_kg + m.agg_14mm_kg + m.agg_20mm_kg)}
                {(m.exposed_rocks || []).length > 0 ? (
                  <> · <span className="text-primary-orange">{m.exposed_rocks.length} rock type{m.exposed_rocks.length > 1 ? "s" : ""}</span> ({m.exposed_rock_kg} kg)</>
                ) : (m.exposed_rock_kg > 0 && <> · Exposed {m.exposed_rock_kg}</>)}
                {(() => {
                  const addCount = ADDITIVE_FIELDS.filter((f) => (Number(m[f.k]) || 0) > 0).length;
                  return addCount > 0 ? <> · <span className="text-primary-orange">{addCount} additive{addCount > 1 ? "s" : ""}</span></> : null;
                })()}
              </div>
            </div>
          );
        })}
      </div>

      <CsvImportDialog entity="mixes" open={importOpen} onOpenChange={setImportOpen} onDone={load} />
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-none bg-surface border-industrial max-w-3xl" data-testid="mix-dialog">
          <DialogHeader><DialogTitle className="heading uppercase">{editing ? "Edit" : "New"} Mix Design</DialogTitle></DialogHeader>
          <div className="grid grid-cols-3 gap-3 max-h-[70vh] overflow-y-auto pr-2">
            <div><Label className="label-tag">Code</Label><Input data-testid="mix-code" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} className="rounded-none bg-transparent border-industrial mono" /></div>
            <div className="col-span-2"><Label className="label-tag">Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-none bg-transparent border-industrial mono" /></div>

            {/* Ingredients */}
            <div className="col-span-3 label-tag border-t border-industrial pt-3 flex items-center gap-2"><FlaskConical className="w-3 h-3" />Ingredients (per m³)</div>
            {ingredientFields.map((f) => (
              <div key={f.k}>
                <Label className="label-tag">{f.l}</Label>
                <Input type="number" step="0.1" value={form[f.k]} onChange={(e) => setForm({ ...form, [f.k]: parseFloat(e.target.value) || 0 })} className="rounded-none bg-transparent border-industrial mono" />
              </div>
            ))}

            {/* v3 · Exposed Rock Types — dynamic rows loaded from Inventory (category=exposed_rock) */}
            <div className="col-span-3 label-tag border-t border-industrial pt-3 flex items-center justify-between gap-2">
              <span className="flex items-center gap-2"><FlaskConical className="w-3 h-3" />Exposed Rock Types (per m³)</span>
              <span className="mono text-[10px] text-muted-industrial normal-case tracking-normal">
                Total {(form.exposed_rocks || []).reduce((s, r) => s + (Number(r.kg_per_m3) || 0), 0).toFixed(1)} kg
              </span>
            </div>
            {rockItems.length === 0 && (
              <div className="col-span-3 text-[11px] text-muted-industrial mono uppercase tracking-widest border border-dashed border-industrial p-2" data-testid="no-exposed-rocks">
                No exposed rock items yet. Add them in <span className="text-primary-orange">Inventory → Category = exposed_rock</span> (e.g. Basalt 10mm, Quartz 14mm, White Marble).
              </div>
            )}
            {rockItems.map((item) => {
              const row = (form.exposed_rocks || []).find((r) => r.item_id === item.id) || { kg_per_m3: 0 };
              return (
                <div key={item.id} className="col-span-3 grid grid-cols-[1fr_130px_120px] gap-2 items-end" data-testid={`rock-row-${item.sku || item.id}`}>
                  <div>
                    <Label className="label-tag">{item.name}</Label>
                    <div className="text-[10px] mono text-muted-industrial mt-0.5">{item.sku || "—"} · Stock {(Number(item.quantity) / 1000).toFixed(2)} t</div>
                  </div>
                  <div>
                    <Label className="label-tag">kg / m³</Label>
                    <Input
                      type="number"
                      step="0.1"
                      value={row.kg_per_m3}
                      onChange={(e) => updateRock(item.id, e.target.value)}
                      className="rounded-none bg-transparent border-industrial mono"
                      data-testid={`rock-input-${item.sku || item.id}`}
                    />
                  </div>
                  <div>
                    <Label className="label-tag">$ / kg</Label>
                    <div className="mono text-sm text-primary-orange pt-2">${(Number(item.unit_cost) || 0).toFixed(3)}</div>
                  </div>
                </div>
              );
            })}
            <div>
              <Label className="label-tag">Slump (mm)</Label>
              <Input type="number" value={form.slump_mm} onChange={(e) => setForm({ ...form, slump_mm: parseInt(e.target.value) || 0 })} className="rounded-none bg-transparent border-industrial mono" />
            </div>
            <div>
              <Label className="label-tag">Strength (MPa)</Label>
              <Input type="number" step="0.5" value={form.strength_mpa} onChange={(e) => setForm({ ...form, strength_mpa: parseFloat(e.target.value) || 0 })} className="rounded-none bg-transparent border-industrial mono" />
            </div>
            <div>
              <Label className="label-tag">Max W/C ratio</Label>
              <Input type="number" step="0.01" min="0.2" max="1" placeholder={form.cement_kg ? `default ${((Number(form.water_kg) + 20) / Number(form.cement_kg)).toFixed(2)}` : "e.g. 0.55"} value={form.max_wc_ratio ?? ""} onChange={(e) => setForm({ ...form, max_wc_ratio: e.target.value })} className="rounded-none bg-transparent border-industrial mono" data-testid="mix-max-wc" />
              <div className="mono text-[9px] text-muted-industrial mt-0.5">Design {form.cement_kg ? (Number(form.water_kg) / Number(form.cement_kg)).toFixed(2) : "—"} · docket flags loads over this limit</div>
            </div>
            <div>
              <Label className="label-tag">Target W/C (design spec)</Label>
              <Input type="number" step="0.01" min="0.2" max="1" placeholder={form.cement_kg ? `auto ${(Number(form.water_kg) / Number(form.cement_kg)).toFixed(2)}` : "e.g. 0.50"} value={form.target_wc_ratio ?? ""} onChange={(e) => setForm({ ...form, target_wc_ratio: e.target.value })} className="rounded-none bg-transparent border-industrial mono" data-testid="mix-target-wc" />
              <div className="mono text-[9px] text-muted-industrial mt-0.5">Leave blank to lock at design water÷cement · used to compare actual loads</div>
            </div>
            <div>
              <Label className="label-tag flex items-center gap-1"><DollarSign className="w-3 h-3" />Sale / m³</Label>
              <Input type="number" step="0.5" value={form.price_per_m3} onChange={(e) => setForm({ ...form, price_per_m3: parseFloat(e.target.value) || 0 })} className="rounded-none bg-transparent border-industrial mono" data-testid="mix-price" />
            </div>

            {/* Additives — v3.5 · Fixed 4-line dosing skid (WRDA PA / WRDA PN40 / AEA / DARASET NC90).
                All doses are in millilitres per m³, matching the plant's Rinstrum-fed metering pumps. */}
            <div className="col-span-3 label-tag border-t border-industrial pt-3 flex items-center gap-2">
              <Beaker className="w-3 h-3" />Additives (ml per m³)
            </div>
            {ADDITIVE_FIELDS.map((f) => (
              <div key={f.k} data-testid={`additive-row-${f.k}`}>
                <Label className="label-tag">{f.l}</Label>
                <Input
                  type="number"
                  step="0.1"
                  min="0"
                  value={form[f.k] ?? 0}
                  onChange={(e) => setForm({ ...form, [f.k]: parseFloat(e.target.value) || 0 })}
                  className="rounded-none bg-transparent border-industrial mono"
                  data-testid={`additive-input-${f.k}`}
                />
                <div className="text-[9px] mono uppercase tracking-widest text-muted-industrial mt-0.5">{f.hint} · ml/m³</div>
              </div>
            ))}

            {/* Batch plant */}
            <div className="col-span-3 label-tag border-t border-industrial pt-3">Batch plant · Rinstrum K412</div>
            <div className="col-span-2">
              <Label className="label-tag">K412 recipe (product name exactly as on the panel)</Label>
              <Input data-testid="mix-k412-product" list="k412-products" value={form.k412_product || ""}
                onChange={(e) => setForm({ ...form, k412_product: e.target.value.toUpperCase() })}
                placeholder="e.g. 32-14MM" className="rounded-none bg-transparent border-industrial mono" />
              <datalist id="k412-products">{k412Products.map((n) => <option key={n} value={n} />)}</datalist>
              <div className="mono text-[9px] text-muted-industrial uppercase mt-1">When targets are sent, the panel is switched to this recipe first. Leave blank to use whatever recipe the panel shows.</div>
            </div>

            {/* Accounting */}
            <div className="col-span-3 label-tag border-t border-industrial pt-3">Accounting</div>
            <div className="col-span-2">
              <Label className="label-tag">GL / Chart of Accounts Code (Xero / MYOB export)</Label>
              <Input data-testid="mix-gl-code" value={form.gl_code} onChange={(e) => setForm({ ...form, gl_code: e.target.value })} placeholder="e.g. 4100 · Sales - Concrete" className="rounded-none bg-transparent border-industrial mono" />
            </div>
            <div>
              <Label className="label-tag">MYOB Item Number</Label>
              <Input data-testid="mix-myob-item" value={form.myob_item_number} onChange={(e) => setForm({ ...form, myob_item_number: e.target.value })} placeholder="e.g. 4014MPA" className="rounded-none bg-transparent border-industrial mono" />
              <div className="text-[10px] mono uppercase tracking-widest text-muted-industrial mt-1">
                Exact code from your MYOB Items list — used in MYOB CSV exports so imports match.
              </div>
            </div>
          </div>

          {/* Live cost preview from current form values won't be computed on frontend — the backend
              recomputes on save. This banner explains the flow. */}
          {editing && (
            <div className="border border-industrial p-2 flex items-center gap-3 text-xs">
              <TrendingUp className="w-3.5 h-3.5 text-primary-orange shrink-0" />
              <div className="text-muted-industrial mono">
                Cost <span className="text-red-500 font-bold">${(editing.material_cost_per_m3 || 0).toFixed(2)}</span>
                {" · "}Profit <span className="font-bold" style={{ color: (form.price_per_m3 - (editing.material_cost_per_m3 || 0)) >= 0 ? "#10B981" : "#EF4444" }}>${(form.price_per_m3 - (editing.material_cost_per_m3 || 0)).toFixed(2)}</span>
                {" · "}Saves re-compute cost from current inventory unit prices.
              </div>
            </div>
          )}

          <DialogFooter>
            <Button data-testid="save-mix-btn" onClick={save} className="rounded-none bg-primary-orange hover:bg-[rgb(var(--primary-hover))] uppercase tracking-widest text-xs font-bold">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
